"use client"

import {type Step1Data, step1Schema} from "../../../../../schemas/auth"
import {useRouter} from "next/navigation";
import {useRef, useState} from "react";
import {toast} from "sonner"
import {Step1Account} from "./step-1-account";
import {FormRef, isSuccessResult} from "@/components/form/utils";
import {updateRequestAccount} from "@/api/core/actions/accounts/updateRequestAccount";
import {requestNewAccount} from "@/api/core/actions/accounts/requestNewAccount";
import {UserType} from "@/api/core/lib/dbTypes";
import {InvitableUserType} from "@/api/core/lib/userTypes";
import {sendLinkToContinueAccountRequest} from "@/api/core/actions/accounts/sendLinkToContinueAccountRequest";
import type {servicesDictionary} from "@/api/core/lib/servicesDictionary";


export type Account = Awaited<ReturnType<typeof servicesDictionary.accountRequestsService.getRequestInfo>>

interface Step1AccountWrapperProps {
  account?: Account
  referralKey?: string,
  accountType?: UserType,
}

async function socialLogin(provider: "google" | "facebook") {
  return {success: true}
}

export function Step1AccountWrapper({account, referralKey, accountType = UserType.CUSTOMER}: Step1AccountWrapperProps) {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const handleEmailSignUp = async (data: Step1Data) => {
    setLoading(true)
    // The account type comes from the referral link that originated the request (defaults to CUSTOMER when there is
    // none); referral links are only ever created with an invitable type, never `founder`.
    const invitableAccountType = accountType as InvitableUserType
    const result = account ?
      await updateRequestAccount({...data, accountType: invitableAccountType})
      :
      await requestNewAccount({...data, accountType: invitableAccountType, referralKey})
    if (isSuccessResult(result)) {
      router.push(`/account-request/step2`)
    } else {
      if (result.error.message.includes('already started')) {
        toast.warning("Oops! It looks like you’ve already started signing up with this email.", {
          description: "To keep going, just click the button — we’ll send a link to your inbox so you can continue your application.",
          dismissible: true,
          closeButton: true,
          duration: 100000,
          action: {
            label: "Send link",
            onClick: () => handleSendLinkToContinueAccountRequest(data.email),
          },
        })
        return {error: {message: ""}}
      }
    }
    setLoading(false)
    return result
  }
  const form = useRef<FormRef<typeof step1Schema, typeof handleEmailSignUp> | undefined>(undefined)

  const handleSendLinkToContinueAccountRequest = async (email: string) => {
    const result = await sendLinkToContinueAccountRequest({email})

    if (isSuccessResult(result)) {
      toast.success("Account request sent.!", {description: "please check your email and click the link to continue"})
    } else {
      toast.error("An unexpected error has occurred.!", {description: "Review your internet connection and try again.",})
    }
  }

  const handleSocialSignUp = async (provider: "google" | "facebook") => {
    setLoading(true)
    try {
      const result = await socialLogin(provider)
      if (result.success) {
        toast(`Signed in with ${provider}!`)
        // Skip to step 2 for social login
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast("Social login failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Step1Account
      ref={form}
      account={account}
      handleSocialSignUp={handleSocialSignUp}
      handleEmailSignUp={handleEmailSignUp}
      loading={loading}/>
  )
}
